/**
 * Stories implemented
 */
/* eslint-disable local-rules/require-storybook-documentation */
import { Box, Image } from '@zillow/constellation-10';
import { CYPRESS_INTEGRATION_TEST_IDS } from 'src/common/constants';
import { setCypressID } from 'src/utils/setCypressID';
import { Skeleton } from 'src/components/Skeleton/Skeleton';
import { ZHLPersonalizedNextStepState } from 'src/services/graphql/generated/graphql';
import { FunctionComponentWithChildren } from 'src/utils/types';
import {
    IMAGE_DIMENSIONS_DESIGN_SPECIFIED_BREAKPOINTS,
    DESIGN_BREAKPOINT_TO_IMAGE_HEIGHT,
    APPLICATION_LIFECYCLE_STATUS_TO_IMAGES,
} from './constants';
import { isValidApplicationLifecycleStatus } from './typeguards';

type HeroGraphicProps = {
    /**
     * What personalized next step graphic to show
     */
    status: ZHLPersonalizedNextStepState | null;
};

/**
 * This micro-frontend module renders a unique hero illustration for each Personalized Next Step, using
 * an incrementally higher resolution image over time to achieve better FCP and LCP times while still having
 * a high fidelity image.
 */
export const HeroGraphic: FunctionComponentWithChildren<HeroGraphicProps> = ({ status, children }) => {
    if (!status) {
        return (
            <Skeleton
                card
                height={Object.values(IMAGE_DIMENSIONS_DESIGN_SPECIFIED_BREAKPOINTS).reduce(
                    (acc, breakpoint) => ({
                        ...acc,
                        [breakpoint]: DESIGN_BREAKPOINT_TO_IMAGE_HEIGHT[breakpoint],
                    }),
                    {} as Record<string, string>
                )}
                width="100%"
            />
        );
    }

    if (!isValidApplicationLifecycleStatus(status)) {
        return null;
    }

    const image = APPLICATION_LIFECYCLE_STATUS_TO_IMAGES[status];

    return (
        <Box css={{ position: 'relative' }}>
            <Box css={{ borderRadius: '8px', overflow: 'hidden' }}>
                <Image
                    src={image.src}
                    alt="Home loans dashboard hero graphic"
                    css={{
                        position: 'relative',
                        objectFit: 'fill',
                        width: '100%',
                        zIndex: 1,
                        maxHeight: '{heroGraphicMaxHeight}',
                    }}
                    {...setCypressID(CYPRESS_INTEGRATION_TEST_IDS.MORTGAGE_DASHBOARD.HERO_IMAGE)}
                    aria-hidden="true"
                />
            </Box>
            <Box
                css={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 2,
                }}>
                {children}
            </Box>
        </Box>
    );
};
